import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartContainer } from '~/pages/Farmer/Dashboard/style';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useNavigate, useParams } from 'react-router-dom';
import FutureContractsService from '~/api/services/futureContracts';
import Layout from '~/components/Layout';
import dayjs from 'dayjs';
import { FiltersWrapper, StyledHeader } from './style'
import { ThemeContext } from 'styled-components';
import { Dropdown } from 'semantic-ui-react';
import { DateInput, MonthInput, YearInput } from 'semantic-ui-calendar-react';
import { useAuth } from '~/routes/components/AuthProvider';
import { formatNumberWithoutFractions, formatNumber, capitalizeFirstLetter, handleErrors } from '~/utils/helpers';
import { DropdownOptionsType } from '~/types';

class CustomizedAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, payload, fill } = this.props;
    const noResults = (payload?.value === 'auto')
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor='end' fill={fill} transform={noResults || (payload?.value === 0) ? 'rotate(0)' : 'rotate(-45)'}>
          {noResults ? 'N/A' : payload.value}
        </text>
      </g>
    );
  }
}

class CustomizedYAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, payload, fill } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={5} dy={0} textAnchor='end' fill={fill} transform='rotate(0)'>
          {`${formatNumberWithoutFractions(payload.value)}`}
        </text>
      </g>
    );
  }
}

const initialMarginsSymbol = {
  text: 'Ife KFMV (KFMV)',
  value: 'KFMV'
}

const FutureContractsChart: React.FC = () => {
  const { t } = useTranslation();
  const { symbol, contractsType, month, year } = useParams();
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const { firstColor } = useContext(ThemeContext)
  const navigate = useNavigate()

  const [isBusy, setIsBusy] = useState(false);
  const [showData, setShowData] = useState(null);
  const [chartDomain, setChartDomain] = useState({
    dataMin: 0,
    dataMax: 0,
    divider: 0
  })
  const [productOptions, setProductOptions] = useState<DropdownOptionsType[]>([])
  const [dataFiltered, setDataFiltered] = useState({
    symbol: symbol,
    year: year,
    month: parseInt(month),
    dateFrom: '',
    dateTo: ''
  })

  useEffect(() => {
    fetchShowData();
  }, []);

  useEffect(() => {
    fetchShowData()
  }, [dataFiltered])

  async function fetchShowData() {
    setIsBusy(true);
    try {
      if (productOptions.length === 0) {
        const responseOptions = await FutureContractsService.getIndexData(contractsType)
        const productOptions = responseOptions.data?.symbols?.map(symbol => ({
          text: `${symbol.label} (${symbol.abbreviation})`,
          value: symbol.abbreviation
        })) || [initialMarginsSymbol]
        setProductOptions(productOptions)
      }
      const showDataResponse = await FutureContractsService.getDataTradingMonth(contractsType, {
        ...dataFiltered,
        dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
        dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
      });
      const key = contractsType === 'initial-margins' ? 'priceScanRange' : 'settlement'
      const min = Math.min(...showDataResponse.data.map(item => item[key]))
      const max = Math.max(...showDataResponse.data.map(item => item[key]))
      const divider = Math.pow(10, Math.floor(max).toString().length - 2)
      setShowData(showDataResponse.data);
      setChartDomain({
        dataMin: Math.floor(min / divider) * divider,
        dataMax: Math.floor(max / divider) * divider,
        divider
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  function getHeader() {
    const { year, month } = dataFiltered
    const date = new Date(parseInt(year), month - 1)
    return capitalizeFirstLetter(dayjs(date).format('MMM YYYY'))
  }

  function renderChart() {
    const data = showData.map((item) => {
      return {
        ...item,
        timestamp: dayjs(item.timestamp).format('DD.MM.YYYY'),
        date: dayjs(item.date).format('DD.MM.YYYY')
      };
    });
    const product = productOptions.find(product => product.value === dataFiltered.symbol)
    return (
      <>
        <ChartContainer style={{ margin: 'auto', paddingTop: 60 }}>
          <StyledHeader>{`${product?.text} - ${getHeader()}`}</StyledHeader>
          <ResponsiveContainer width='100%' height='85%'>
            <LineChart
              width={500}
              height={300}
              data={data}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }}
            >
              <CartesianGrid strokeDasharray='1 1' />
              <XAxis
                height={65}
                tick={<CustomizedAxisTick />}
                dataKey={`${contractsType === 'initial-margins' ? 'date' : 'timestamp'}`}
              />
              <YAxis
                tickCount={3}
                tick={<CustomizedYAxisTick />}
                domain={[chartDomain.dataMin - chartDomain.divider, chartDomain.dataMax + chartDomain.divider]}
                label={{ value: t(('Value (in €)')), angle: -90, position: 'insideLeft' }}
                dataKey={`${contractsType === 'initial-margins' ? 'priceScanRange' : 'settlement'}`}
              ></YAxis>
              <Tooltip separator=': '
                formatter={(value, name, props) => {
                  if (name === t('Value (in €)')) {
                    return [`${formatNumber(value)}`, name]
                  }
                  return [value, name]
                }} />
              <Legend />
              <Line
                type='monotone'
                dataKey={`${contractsType === 'initial-margins' ? 'priceScanRange' : 'settlement'}`}
                stroke={firstColor}
                dot={{ r: 5 }}
                activeDot={{ r: 8 }}
                name={t('Value (in €)')}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </>
    );
  }

  function getTitle() {
    switch (contractsType) {
      case 'ife-quotes':
        return t('Ife stock milk value')
      case 'initial-margins':
        return t('Initial margin')
      case 'eod-quotes':
        return t('EEX Setlements')
    }
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function getNameOfMonthFromIndex(monthValue) {
    if (monthValue === '' || isNaN(monthValue)) return ''
    const date = new Date(2009, monthValue - 1, 10)
    const month = date.toLocaleString(dayjs.locale(), { month: 'long' });
    return month
  }

  function getDateTo(dateFrom) {
    const { dateTo } = dataFiltered
    if (dateFrom && dateTo && dayjs(dateFrom).diff(dayjs(dateTo)) > 0) {
      return dayjs(dateFrom).format('DD.MM.YYYY')
    }
    return dateTo === '' ? dayjs(getDateWithTime(dateFrom)).format('DD.MM.YYYY') : dateTo
  }

  function getDateWithTime(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    return new Date(year, month - 1, day)
  }

  function renderFilters() {
    return <FiltersWrapper>
      <Dropdown
        placeholder={t('Select product')}
        options={productOptions}
        value={dataFiltered.symbol}
        selectOnBlur={false}
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          //@ts-ignore
          symbol: data.value
        })}
        selection
      />
      <YearInput
        name='date'
        autoComplete='off'
        placeholder={t('Expiration year')}
        closable
        minDate={'2004'}
        maxDate={'2024'}
        onKeyPress={handleKeyPress}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        className={'datePicker'}
        value={dataFiltered.year}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          year: value
        })}
      />
      <MonthInput
        name='date'
        autoComplete='off'
        placeholder={t('Expiration month')}
        closable
        onKeyPress={handleKeyPress}
        dateFormat={'MM'}
        style={{ marginRight: 10, marginBottom: 5 }}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        className={'datePicker'}
        value={getNameOfMonthFromIndex(dataFiltered.month)}
        iconPosition='left'
        onChange={(event, data) => {
          setDataFiltered({
            ...dataFiltered,
            //@ts-ignore
            month: parseInt(data.value)
          })
        }}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        onKeyPress={handleKeyPress}
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateFrom: value
        })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date to')}
        closable
        clearIcon={'close'}
        minDate={dataFiltered.dateFrom}
        clearable
        initialDate={getDateTo(dataFiltered.dateFrom)}
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateTo}
        iconPosition='left'
        onChange={(event, { name, value }) => {
          setDataFiltered({
            ...dataFiltered,
            dateTo: value
          })
        }}
      />
    </FiltersWrapper>
  }

  return (
    <Layout
      segmented
      title={getTitle()}
      filters={renderFilters()}
      close={() => navigate(-1)}
      isBusy={isBusy}
    >
      {showData && renderChart()}
    </Layout>
  );
};

export default FutureContractsChart;
